import type { RadioGroupOption } from "@Light/components/system/RadioGroup";
import { RadioGroup } from "@Light/components/system/RadioGroup";
import type { Utility } from "@Light/services/lightTypes";
import { getUtilityBehavior } from "@Light/utils/utility";

export const OTHER_UTILITY = "other" as const;

export type UtilityChoice = Utility | typeof OTHER_UTILITY;

export function makeUtilityChoiceSubtitle(
  postalCode: string | undefined,
  utilities: Utility[],
): string | undefined {
  return utilities.length > 1
    ? `${postalCode ? `Zip code ${postalCode}` : "Your zip code"} is split between utility providers. We need your local utility for accurate pricing.`
    : undefined;
}

export function canUtilityChoiceProvideAddress(utilities: Utility[]): boolean {
  return utilities.every(
    (utility) =>
      getUtilityBehavior(utility.id).serviceLocationEntry === "search",
  );
}

export function utilityChoiceHelpButtonText(utilities: Utility[]): string {
  return canUtilityChoiceProvideAddress(utilities)
    ? "Have a question?"
    : "Not sure? Reach out to us";
}

export type UtilityRadioGroupProps = {
  name: string;
  utilities: Utility[];
  value?: UtilityChoice;
  onChange: (choice: UtilityChoice) => void;
  onClickProvideAddress?: () => void;
};

export const UtilityRadioGroup = ({
  name,
  utilities,
  value,
  onChange,
  onClickProvideAddress,
}: UtilityRadioGroupProps) => {
  const options: RadioGroupOption<UtilityChoice>[] = [
    ...utilities.map((utility) => ({
      value: utility,
      label: utility.full_name,
    })),
    ...(utilities.length === 1
      ? [
          {
            value: OTHER_UTILITY,
            label: "A different utility provider",
            testId: "radio-option-other-utility",
          },
        ]
      : []),
  ];
  return (
    <div className="flex flex-col gap-3">
      <RadioGroup
        name={name}
        options={options}
        value={value}
        onChange={onChange}
      />
      {canUtilityChoiceProvideAddress(utilities) && onClickProvideAddress ? (
        <button
          type="button"
          onClick={onClickProvideAddress}
          data-testid="zip-utility-provide-address"
          className="cursor-pointer text-center text-sm text-tertiary underline underline-offset-4"
        >
          Provide your address instead
        </button>
      ) : null}
    </div>
  );
};
